import { useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "@/context/AuthContext";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

/* ── Google logo SVG ──────────────────────────────────────────── */
const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const EyeOpen = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosed = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const DOT_GRID =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='36'%3E%3Ccircle cx='18' cy='18' r='1' fill='rgba(90,144,168,0.10)'/%3E%3C/svg%3E\")";

const fieldBase = (focused) => ({
  display: "block",
  width: "100%",
  padding: "13px 16px",
  border: `1px solid ${focused ? "#5A90A8" : "rgba(90,144,168,0.25)"}`,
  borderRadius: 10,
  fontSize: 14,
  fontFamily: "Inter, sans-serif",
  color: "#1C3545",
  background: "#F5FAFC",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 200ms ease, box-shadow 200ms ease",
  boxShadow: focused ? "0 0 0 3px rgba(90,144,168,0.12)" : "none",
});

const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 500,
  color: "#4D7A8E",
  marginBottom: 8,
  fontFamily: "Inter, sans-serif",
};

/* ── Google button — isolated so useGoogleLogin only mounts when client ID exists ── */
function GoogleSignInButton({ onSuccess, onError, disabled }) {
  const googleLogin = useGoogleLogin({ onSuccess, onError });
  return (
    <button
      type="button"
      onClick={() => googleLogin()}
      disabled={disabled}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        width: "100%", padding: "12px 16px",
        background: "#FFFFFF",
        color: "#1f1f1f", border: "1px solid rgba(90,144,168,0.25)", borderRadius: 10,
        fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background 200ms ease, transform 200ms ease, box-shadow 200ms ease",
      }}
      onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.background = "#F5FAFC"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(90,144,168,0.15)"; } }}
      onMouseLeave={(e) => { if (!disabled) { e.currentTarget.style.background = "#FFFFFF"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}
    >
      <GoogleLogo />
      {disabled ? "Please wait…" : "Continue with Google"}
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════ */
export default function LoginPage({ onBack, audience, onCreateAccount, onLoginSuccess, onForgotPassword }) {
  const auth = useAuth();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword]     = useState("");
  const [showPw, setShowPw]         = useState(false);
  const [idFocused, setIdFocused]   = useState(false);
  const [pwFocused, setPwFocused]   = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");

  const canSubmit = !loading && identifier.trim().includes("@") && password.length >= 1;

  /* ── Email/password login ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setLoading(true);
    try {
      const user = await auth.login(identifier.trim(), password);
      if (onLoginSuccess) onLoginSuccess(user);
      else onBack();
    } catch (err) {
      if (!err.response) {
        setError("Unable to reach the server. Check your connection and try again.");
      } else {
        const raw = err.response.data?.detail;
        const msg = Array.isArray(raw)
          ? raw.map((e) => e.msg).join("; ")
          : typeof raw === "string"
          ? raw
          : "Login failed. Please try again.";
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Google login callbacks ── */
  const handleGoogleSuccess = async (tokenResponse) => {
    setError("");
    try {
      const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
        headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch Google profile");
      const profile = await res.json();
      const user = await auth.socialLogin({
        provider: "google",
        provider_id: profile.sub,
        email: profile.email,
        name: profile.name,
        role: audience === "doctors" ? "doctor" : "patient",
      });
      if (onLoginSuccess) onLoginSuccess(user);
      else onBack();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(msg ? (typeof msg === "string" ? msg : "Sign-in failed.") : "Google sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google sign-in was cancelled or failed.");
    setLoading(false);
  };

  /* ── render ── */
  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        background: "#EDF4F7",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
        paddingTop: "140px",
        overflow: "hidden",
      }}
    >
      <div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: DOT_GRID, backgroundRepeat: "repeat", pointerEvents: "none", zIndex: 1 }} />

      {/* Card */}
      <div
        style={{
          position: "relative",
          zIndex: 2,
          background: "#FFFFFF",
          border: "1px solid rgba(90,144,168,0.18)",
          borderRadius: 22,
          padding: "40px 36px 36px",
          width: "100%",
          maxWidth: 440,
          boxShadow: "0 4px 32px rgba(90,144,168,0.12)",
        }}
      >
        {/* Brand wordmark */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <button
            type="button"
            onClick={onBack}
            className="font-serif-display"
            style={{ fontSize: 19, color: "#1C3545", background: "none", border: "none", cursor: "pointer", letterSpacing: "-0.035em", padding: 0 }}
          >
            Supercore Longevity
          </button>
          <div aria-hidden style={{ width: 28, height: 2, background: "#8AAEBB", borderRadius: 99, margin: "10px auto 0", opacity: 0.8 }} />
        </div>

        <h1 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>
          Welcome back
        </h1>
        <p style={{ margin: "0 0 28px", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>
          Don't have an account?{" "}
          <button
            type="button"
            onClick={onCreateAccount}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#5A90A8", fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif", padding: 0, transition: "color 150ms ease" }}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
          >
            Create one
          </button>
        </p>

        {/* Error banner */}
        {error && (
          <div style={{ marginBottom: 18, padding: "11px 14px", borderRadius: 9, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 13, fontFamily: "Inter, sans-serif", lineHeight: 1.45 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Email */}
          <div style={{ marginBottom: 18 }}>
            <label htmlFor="sc-identifier" style={labelStyle}>Email address</label>
            <input
              id="sc-identifier"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              onFocus={() => setIdFocused(true)}
              onBlur={() => setIdFocused(false)}
              style={fieldBase(idFocused)}
              disabled={loading}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <label htmlFor="sc-password" style={{ ...labelStyle, marginBottom: 0 }}>Password</label>
              <button
                type="button"
                onClick={onForgotPassword}
                style={{ fontSize: 12, color: "#9BBCCC", background: "none", border: "none", cursor: "pointer", fontFamily: "Inter, sans-serif", padding: 0, transition: "color 150ms ease" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#5A90A8")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#9BBCCC")}
              >
                Forgot password?
              </button>
            </div>
            <div style={{ position: "relative" }}>
              <input
                id="sc-password"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPwFocused(true)}
                onBlur={() => setPwFocused(false)}
                style={{ ...fieldBase(pwFocused), paddingRight: 44 }}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Hide password" : "Show password"}
                style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: showPw ? "#5A90A8" : "#9BBCCC", padding: 0, display: "flex", alignItems: "center", transition: "color 150ms ease" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#5A90A8")}
                onMouseLeave={(e) => (e.currentTarget.style.color = showPw ? "#5A90A8" : "#9BBCCC")}
              >
                {showPw ? <EyeClosed /> : <EyeOpen />}
              </button>
            </div>
          </div>

          {/* Sign In button */}
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              display: "block", width: "100%", padding: "13px",
              background: canSubmit ? "#5A90A8" : "rgba(90,144,168,0.25)",
              color: canSubmit ? "#FFFFFF" : "rgba(90,144,168,0.5)",
              border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700,
              fontFamily: "Inter, sans-serif", cursor: canSubmit ? "pointer" : "not-allowed",
              letterSpacing: "0.01em", transition: "background 200ms ease, transform 200ms ease, box-shadow 200ms ease",
            }}
            onMouseEnter={(e) => { if (canSubmit) { e.currentTarget.style.background = "#4D7A8E"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(90,144,168,0.3)"; } }}
            onMouseLeave={(e) => { if (canSubmit) { e.currentTarget.style.background = "#5A90A8"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        {/* Google sign-in */}
        {GOOGLE_CLIENT_ID && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "24px 0" }}>
              <div style={{ flex: 1, height: 1, background: "rgba(90,144,168,0.15)" }} />
              <span style={{ fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif", letterSpacing: "0.04em" }}>or</span>
              <div style={{ flex: 1, height: 1, background: "rgba(90,144,168,0.15)" }} />
            </div>
            <GoogleSignInButton
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              disabled={loading}
            />
          </>
        )}

        <p style={{ textAlign: "center", fontSize: 11, color: "#A8C8D8", marginTop: 24, marginBottom: 0, lineHeight: 1.6, fontFamily: "Inter, sans-serif" }}>
          By continuing you agree to our{" "}
          <a href="#terms" style={{ color: "#7AAABB", textDecoration: "underline" }}>Terms</a>
          {" "}and{" "}
          <a href="#privacy" style={{ color: "#7AAABB", textDecoration: "underline" }}>Privacy Policy</a>.
        </p>
      </div>

      {/* Back link */}
      <button
        type="button"
        onClick={onBack}
        style={{ position: "relative", zIndex: 2, marginTop: 24, background: "none", border: "none", cursor: "pointer", color: "#9BBCCC", fontSize: 13, fontFamily: "Inter, sans-serif", display: "flex", alignItems: "center", gap: 6, transition: "color 150ms ease" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#1C3545")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#9BBCCC")}
      >
        ← Back to {audience === "doctors" ? "Doctor" : "Patient"} Home
      </button>
    </div>
  );
}
